import * as THREE from 'three';
import type {
    ProjectedModelConicSnapPointCandidate,
    ProjectedModelConicSnapPointResolveInput,
    ProjectedModelConicSnapPointResolverPort,
} from '../../../types/sketch/VisibleModelEdgeProjection';
import {
    TopologySampledCurveFittingMathService,
} from '../../reference/sketch-entity-reference/source/projection/fitting';
import type {
    TopologyAngleTraceResult,
    TopologyCircleFitResult,
    TopologyEllipseFitResult,
    TopologyFittingLocalPoint2,
} from '../../reference/sketch-entity-reference/source/projection/fitting';

/** 圆拟合半径残差相对投影曲线尺度的容差。 */
const CIRCLE_RELATIVE_RESIDUAL_TOLERANCE = 1e-6;

/** 圆拟合半径残差绝对下限。 */
const CIRCLE_ABSOLUTE_RESIDUAL_TOLERANCE = 1e-8;

/** 椭圆归一化二次曲线方程最大残差。 */
const ELLIPSE_MAX_RESIDUAL_TOLERANCE = 1e-3;

/** 长短轴比接近 1 时将投影曲线视为圆。 */
const CIRCLE_AXIS_RATIO_TOLERANCE = 1e-5;

/** 次轴相对主轴低于该值时视为退化直线，不生成圆锥曲线参考点。 */
const CONIC_DEGENERATE_AXIS_RATIO = 1e-6;

/** 参数角范围判定容差。 */
const ANGLE_RANGE_TOLERANCE = 1e-6;

/** 圆/椭圆四个象限参数角。 */
const QUADRANT_PARAMETER_ANGLES = [
    0,
    Math.PI * 0.5,
    Math.PI,
    Math.PI * 1.5,
] as const;

/**
 * 投影模型圆锥曲线参考点解析器。
 *
 * 职责：
 * 1. 在目标草图平面局部坐标中拟合投影后的圆或椭圆；
 * 2. 生成符合投影几何的中心与四个轴端象限候选；
 * 3. 对具有真实拓扑端点的修剪曲线，仅保留实际位于参数轨迹内的象限点；
 * 4. 把象限点回映射到源边归一化参数，供上层继续执行可见区间过滤。
 *
 * 本解析器不创建场景对象，也不处理遮挡、捕捉设置或端点/中点语义。
 */
export class ProjectedModelConicSnapPointResolver implements ProjectedModelConicSnapPointResolverPort {
    /** 复用现有拓扑曲线拟合数学，避免模型捕捉域重复实现圆锥拟合。 */
    private readonly math = new TopologySampledCurveFittingMathService();

    /**
     * 解析投影圆锥曲线的中心和象限点。
     *
     * @param input 投影采样点、源参数和拓扑语义。
     * @returns 可进入模型边参考点过滤链的中心/象限候选。
     */
    public resolve(
        input: ProjectedModelConicSnapPointResolveInput,
    ): ProjectedModelConicSnapPointCandidate[] {
        if (input.curveKind !== 'circle' && input.curveKind !== 'ellipse') {
            return [];
        }
        if (
            input.projectedSamplePoints.length < 3
            || input.projectedSamplePoints.length !== input.sourceParameters.length
        ) {
            return this.resolveSourceCenterFallback(input);
        }

        const localPoints = input.projectedSamplePoints.map((point) => {
            const local = input.referencePlane.worldToLocal(point);
            return { x: local.x, y: local.y };
        });
        if (this.countDistinctLocalPoints(localPoints) < 3) {
            return this.resolveSourceCenterFallback(input);
        }

        const scale = this.resolveLocalPointScale(localPoints);
        const circle = this.math.fitCircle(localPoints);
        const ellipse = localPoints.length >= 5 ? this.math.fitEllipse(localPoints) : null;
        const shouldUseCircle = this.shouldUseCircleFit(circle, ellipse, scale);
        if (shouldUseCircle && circle) {
            return this.resolveCircleCandidates(input, localPoints, circle);
        }
        if (ellipse && this.isUsableEllipseFit(ellipse)) {
            return this.resolveEllipseCandidates(input, localPoints, ellipse);
        }
        return this.resolveSourceCenterFallback(input);
    }

    /**
     * 在二维投影退化或拟合失败时，从源三维曲线恢复中心。
     *
     * 圆/椭圆侧视时可能投影为直线，此时二维圆锥拟合无法稳定解析两条轴，
     * 但中心仍然是唯一且可投影的捕捉语义。因此先在源曲线平面拟合中心，
     * 再把该中心正交投影到草图平面；退化情况不生成不稳定的象限点。
     *
     * @param input 投影圆锥曲线解析输入。
     * @returns 可用的投影中心候选；源曲线也无法稳定拟合时返回空数组。
     */
    private resolveSourceCenterFallback(
        input: ProjectedModelConicSnapPointResolveInput,
    ): ProjectedModelConicSnapPointCandidate[] {
        const sourcePoints = input.sourceSamplePoints
            .filter((point) => Number.isFinite(point.x)
                && Number.isFinite(point.y)
                && Number.isFinite(point.z))
            .map((point) => [point.x, point.y, point.z] as [number, number, number]);
        if (sourcePoints.length < 3 || this.math.countDistinctPoints(sourcePoints) < 3) {
            return [];
        }
        const sourceFrame = this.math.createPlaneFrame(sourcePoints);
        if (!sourceFrame) {
            return [];
        }
        const sourceLocalPoints = sourcePoints.map((point) => (
            this.math.projectToPlane(point, sourceFrame)
        ));

        let centerX: number;
        let centerY: number;
        if (input.curveKind === 'circle') {
            const circle = this.math.fitCircle(sourceLocalPoints);
            const scale = this.resolveLocalPointScale(sourceLocalPoints);
            if (!circle || !this.shouldUseCircleFit(circle, null, scale)) {
                return [];
            }
            centerX = circle.centerX;
            centerY = circle.centerY;
        } else {
            const ellipse = sourceLocalPoints.length >= 5
                ? this.math.fitEllipse(sourceLocalPoints)
                : null;
            if (!ellipse || !this.isUsableEllipseFit(ellipse)) {
                return [];
            }
            centerX = ellipse.centerX;
            centerY = ellipse.centerY;
        }

        const sourceCenter = this.math.localToWorld(
            { x: centerX, y: centerY },
            sourceFrame,
        );
        return [{
            kind: 'center',
            projectedPosition: input.referencePlane.projectWorldPointToPlane(
                new THREE.Vector3(sourceCenter[0], sourceCenter[1], sourceCenter[2]),
            ),
            sourceParameter: null,
        }];
    }

    /**
     * 判断圆拟合是否比椭圆拟合更符合当前投影几何。
     *
     * @param circle 圆拟合结果。
     * @param ellipse 椭圆拟合结果。
     * @param scale 投影曲线局部尺度。
     * @returns 应按圆生成参考点时返回 true。
     */
    private shouldUseCircleFit(
        circle: TopologyCircleFitResult | null,
        ellipse: TopologyEllipseFitResult | null,
        scale: number,
    ): boolean {
        if (!circle) {
            return false;
        }
        const circleTolerance = Math.max(
            CIRCLE_ABSOLUTE_RESIDUAL_TOLERANCE,
            scale * CIRCLE_RELATIVE_RESIDUAL_TOLERANCE,
        );
        if (circle.maxResidual > circleTolerance) {
            return false;
        }
        if (!ellipse || !this.isUsableEllipseFit(ellipse)) {
            return true;
        }
        const axisRatio = Math.min(ellipse.radiusX, ellipse.radiusY)
            / Math.max(ellipse.radiusX, ellipse.radiusY);
        return Math.abs(1 - axisRatio) <= CIRCLE_AXIS_RATIO_TOLERANCE;
    }

    /**
     * 判断椭圆拟合是否稳定且未退化为直线。
     *
     * @param ellipse 椭圆拟合结果。
     * @returns 可用于生成中心/象限点时返回 true。
     */
    private isUsableEllipseFit(ellipse: TopologyEllipseFitResult): boolean {
        if (
            !Number.isFinite(ellipse.radiusX)
            || !Number.isFinite(ellipse.radiusY)
            || ellipse.radiusX <= this.math.epsilon
            || ellipse.radiusY <= this.math.epsilon
            || ellipse.maxResidual > ELLIPSE_MAX_RESIDUAL_TOLERANCE
        ) {
            return false;
        }
        const majorRadius = Math.max(ellipse.radiusX, ellipse.radiusY);
        const minorRadius = Math.min(ellipse.radiusX, ellipse.radiusY);
        return minorRadius / majorRadius > CONIC_DEGENERATE_AXIS_RATIO;
    }

    /**
     * 生成圆投影的中心与象限候选。
     *
     * @param input 解析输入。
     * @param localPoints 投影采样点局部坐标。
     * @param circle 圆拟合结果。
     * @returns 中心与有效象限候选。
     */
    private resolveCircleCandidates(
        input: ProjectedModelConicSnapPointResolveInput,
        localPoints: readonly TopologyFittingLocalPoint2[],
        circle: TopologyCircleFitResult,
    ): ProjectedModelConicSnapPointCandidate[] {
        const trace = input.hasRealEndpoints
            ? this.math.resolveAngleTrace([...localPoints], {
                centerX: circle.centerX,
                centerY: circle.centerY,
                xAxis: { x: 1, y: 0 },
                yAxis: { x: 0, y: 1 },
            })
            : null;
        const candidates: ProjectedModelConicSnapPointCandidate[] = [];

        // 统一为圆、圆弧以及特殊投影为圆的椭圆提供中心候选，由上层 `Center` 门禁决定是否消费。
        candidates.push(this.createCenterCandidate(
            input,
            circle.centerX,
            circle.centerY,
        ));
        QUADRANT_PARAMETER_ANGLES.forEach((angle) => {
            if (trace && !this.isAngleInsideTrace(angle, trace)) {
                return;
            }
            const localPoint = {
                x: circle.centerX + circle.radius * Math.cos(angle),
                y: circle.centerY + circle.radius * Math.sin(angle),
            };
            candidates.push(this.createQuadrantCandidate(input, localPoints, localPoint));
        });
        return candidates;
    }

    /**
     * 生成椭圆投影的中心与象限候选。
     *
     * @param input 解析输入。
     * @param localPoints 投影采样点局部坐标。
     * @param ellipse 椭圆拟合结果。
     * @returns 中心与有效象限候选。
     */
    private resolveEllipseCandidates(
        input: ProjectedModelConicSnapPointResolveInput,
        localPoints: readonly TopologyFittingLocalPoint2[],
        ellipse: TopologyEllipseFitResult,
    ): ProjectedModelConicSnapPointCandidate[] {
        const trace = input.hasRealEndpoints
            ? this.math.resolveEllipseAngleTrace([...localPoints], ellipse)
            : null;
        const candidates: ProjectedModelConicSnapPointCandidate[] = [];

        // 中心捕捉语义不再区分完整/部分椭圆，两者都使用投影后解析椭圆的中心。
        candidates.push(this.createCenterCandidate(
            input,
            ellipse.centerX,
            ellipse.centerY,
        ));
        QUADRANT_PARAMETER_ANGLES.forEach((angle) => {
            if (trace && !this.isAngleInsideTrace(angle, trace)) {
                return;
            }
            const cos = Math.cos(angle);
            const sin = Math.sin(angle);
            const localPoint = {
                x: ellipse.centerX
                    + ellipse.majorAxis.x * ellipse.radiusX * cos
                    + ellipse.minorAxis.x * ellipse.radiusY * sin,
                y: ellipse.centerY
                    + ellipse.majorAxis.y * ellipse.radiusX * cos
                    + ellipse.minorAxis.y * ellipse.radiusY * sin,
            };
            candidates.push(this.createQuadrantCandidate(input, localPoints, localPoint));
        });
        return candidates;
    }

    /**
     * 创建一个投影圆锥曲线中心候选。
     *
     * 中心不在曲线边上，因此不伪造源边参数；是否显示和捕捉由上层
     * `Center` 门禁统一决定。
     *
     * @param input 投影圆锥曲线解析输入。
     * @param centerX 草图平面局部中心 X 坐标。
     * @param centerY 草图平面局部中心 Y 坐标。
     * @returns 不带源边参数的中心点候选。
     */
    private createCenterCandidate(
        input: ProjectedModelConicSnapPointResolveInput,
        centerX: number,
        centerY: number,
    ): ProjectedModelConicSnapPointCandidate {
        return {
            kind: 'center',
            projectedPosition: input.referencePlane.localToWorld(
                new THREE.Vector2(centerX, centerY),
            ),
            sourceParameter: null,
        };
    }

    /**
     * 创建象限点候选并回映射源边参数。
     *
     * @param input 解析输入。
     * @param localPoints 投影采样点局部坐标。
     * @param localPoint 象限点局部坐标。
     * @returns 象限点候选。
     */
    private createQuadrantCandidate(
        input: ProjectedModelConicSnapPointResolveInput,
        localPoints: readonly TopologyFittingLocalPoint2[],
        localPoint: TopologyFittingLocalPoint2,
    ): ProjectedModelConicSnapPointCandidate {
        return {
            kind: 'quadrant',
            projectedPosition: input.referencePlane.localToWorld(
                new THREE.Vector2(localPoint.x, localPoint.y),
            ),
            sourceParameter: this.resolveNearestSourceParameter(
                localPoint,
                localPoints,
                input.sourceParameters,
            ),
        };
    }

    /**
     * 判断规范化象限参数角是否位于有向修剪轨迹内。
     *
     * @param angle 象限参数角。
     * @param trace 有向参数角轨迹。
     * @returns 象限点属于当前圆弧/部分椭圆时返回 true。
     */
    private isAngleInsideTrace(angle: number, trace: TopologyAngleTraceResult): boolean {
        const sweep = trace.endAngle - trace.startAngle;
        if (Math.abs(sweep) >= Math.PI * 2 - ANGLE_RANGE_TOLERANCE) {
            return true;
        }
        const progress = sweep >= 0
            ? this.normalizePositiveAngle(angle - trace.startAngle)
            : this.normalizePositiveAngle(trace.startAngle - angle);
        return progress <= Math.abs(sweep) + ANGLE_RANGE_TOLERANCE;
    }

    /**
     * 把角度规范到 [0, 2π) 范围。
     *
     * @param angle 原始角度。
     * @returns 正向规范角度。
     */
    private normalizePositiveAngle(angle: number): number {
        const period = Math.PI * 2;
        return ((angle % period) + period) % period;
    }

    /**
     * 把投影曲线点回映射到最近采样折线的源边参数。
     *
     * @param target 目标投影点局部坐标。
     * @param points 投影采样折线局部坐标。
     * @param sourceParameters 采样点对应的源边参数。
     * @returns 最近折线点的插值源参数。
     */
    private resolveNearestSourceParameter(
        target: TopologyFittingLocalPoint2,
        points: readonly TopologyFittingLocalPoint2[],
        sourceParameters: readonly number[],
    ): number {
        let bestDistanceSquared = Number.POSITIVE_INFINITY;
        let bestParameter = sourceParameters[0] ?? 0;

        for (let index = 0; index < points.length - 1; index += 1) {
            const start = points[index]!;
            const end = points[index + 1]!;
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const lengthSquared = dx * dx + dy * dy;
            const localParameter = lengthSquared <= this.math.epsilon
                ? 0
                : THREE.MathUtils.clamp(
                    ((target.x - start.x) * dx + (target.y - start.y) * dy) / lengthSquared,
                    0,
                    1,
                );
            const nearestX = start.x + dx * localParameter;
            const nearestY = start.y + dy * localParameter;
            const distanceX = target.x - nearestX;
            const distanceY = target.y - nearestY;
            const distanceSquared = distanceX * distanceX + distanceY * distanceY;
            if (distanceSquared < bestDistanceSquared) {
                bestDistanceSquared = distanceSquared;
                bestParameter = THREE.MathUtils.lerp(
                    sourceParameters[index] ?? 0,
                    sourceParameters[index + 1] ?? 1,
                    localParameter,
                );
            }
        }
        return THREE.MathUtils.clamp(bestParameter, 0, 1);
    }

    /**
     * 统计局部采样点中的不同点数量。
     *
     * @param points 局部采样点。
     * @returns 去重后的点数量。
     */
    private countDistinctLocalPoints(points: readonly TopologyFittingLocalPoint2[]): number {
        const unique: TopologyFittingLocalPoint2[] = [];
        points.forEach((point) => {
            if (!unique.some((candidate) => (
                Math.hypot(candidate.x - point.x, candidate.y - point.y) <= this.math.epsilon
            ))) {
                unique.push(point);
            }
        });
        return unique.length;
    }

    /**
     * 解析投影采样点的二维尺度。
     *
     * @param points 局部采样点。
     * @returns 包围盒最大跨度。
     */
    private resolveLocalPointScale(points: readonly TopologyFittingLocalPoint2[]): number {
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        points.forEach((point) => {
            minX = Math.min(minX, point.x);
            minY = Math.min(minY, point.y);
            maxX = Math.max(maxX, point.x);
            maxY = Math.max(maxY, point.y);
        });
        return Math.max(maxX - minX, maxY - minY, this.math.epsilon);
    }
}
